import { Currency } from '@app/shared/types/enums/currency.enum';
import { QuantityUnits } from '@app/shared/types/enums/quantity-units.enum';

export interface TaskEstimationLikeValues {
  time?: string | null;
  financial?: {
    amount?: number | null;
    currency?: Currency;
  };
  quantity?: {
    amount?: number | null;
    unit?: QuantityUnits;
  };
}

export interface TaskEstimationLikePatchPlan {
  time?: { time: string | null };
  financial?: { amount: number | null; currency: Currency };
  quantity?: { amount: number | null; unit: QuantityUnits };
}

interface NormalizedFinancial {
  amount: number | null;
  currency: Currency | null;
}

interface NormalizedQuantity {
  amount: number | null;
  unit: QuantityUnits | null;
}

function normalizeFinancial(financial?: TaskEstimationLikeValues['financial']): NormalizedFinancial {
  if (!financial || financial.amount === null || financial.amount === undefined || financial.amount <= 0) {
    return { amount: null, currency: null };
  }

  return { amount: financial.amount, currency: financial.currency ?? null };
}

function normalizeQuantity(quantity?: TaskEstimationLikeValues['quantity']): NormalizedQuantity {
  if (!quantity || quantity.amount === null || quantity.amount === undefined || quantity.amount <= 0) {
    return { amount: null, unit: null };
  }

  return { amount: quantity.amount, unit: quantity.unit ?? null };
}

function normalizeTime(time?: string | null): string | null {
  return time || null;
}

/**
 * Builds a patch plan containing only estimation-like fields that changed.
 * Empty subsections (null amount / missing time) are treated as equivalent so
 * untouched form defaults do not trigger unnecessary API calls.
 */
export function buildTaskEstimationLikePatchPlan(
  current: TaskEstimationLikeValues | undefined,
  updated: TaskEstimationLikeValues
): TaskEstimationLikePatchPlan {
  const plan: TaskEstimationLikePatchPlan = {};

  const currentTime = normalizeTime(current?.time);
  const updatedTime = normalizeTime(updated.time);
  if (currentTime !== updatedTime) {
    plan.time = { time: updatedTime };
  }

  const currentFinancial = normalizeFinancial(current?.financial);
  const updatedFinancial = normalizeFinancial(updated.financial);
  if (currentFinancial.amount !== updatedFinancial.amount || currentFinancial.currency !== updatedFinancial.currency) {
    plan.financial = {
      amount: updated.financial?.amount ?? null,
      currency: updated.financial?.currency as Currency
    };
  }

  const currentQuantity = normalizeQuantity(current?.quantity);
  const updatedQuantity = normalizeQuantity(updated.quantity);
  if (currentQuantity.amount !== updatedQuantity.amount || currentQuantity.unit !== updatedQuantity.unit) {
    plan.quantity = {
      amount: updated.quantity?.amount ?? null,
      unit: updated.quantity?.unit as QuantityUnits
    };
  }

  return plan;
}
